<script lang="ts">
    import { onDestroy } from 'svelte';
    import { get } from 'svelte/store';
    import { push } from 'svelte-spa-router';
    import { currentUser, authReady } from '$lib/stores/auth';
    import {
        adminListInvites,
        adminCreateInvite,
        adminRevokeInvite,
        humanizeError,
        type AdminInvite,
        type AdminInviteCreateResult,
    } from '$lib/api';
    import AdminNav from '../components/AdminNav.svelte';
    import CopyableUrl from '../components/CopyableUrl.svelte';

    let invites: AdminInvite[] = [];
    let loading = true;
    let error: string | null = null;
    let busyId: string | null = null;

    // Create form state
    let expiresIn = '7d';
    let maxUses = 1;
    let role: 'admin' | 'volunteer' = 'volunteer';
    let note = '';
    let creating = false;
    let createError: string | null = null;
    let createdInvite: AdminInviteCreateResult | null = null;

    async function load() {
        if (!get(authReady)) return;
        if (!$currentUser || $currentUser.role !== 'admin') {
            push('/map');
            return;
        }
        loading = true;
        error = null;
        try {
            invites = await adminListInvites();
        } catch (e: any) {
            error = humanizeError(e, 'Failed to load invites');
        } finally {
            loading = false;
        }
    }

    function fmtTime(s: string | null): string {
        if (!s) return '—';
        return new Date(s).toLocaleString([], {
            month: 'short', day: 'numeric', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    }

    function inviteState(inv: AdminInvite): { label: string; cls: string } {
        if (inv.revoked_at) return { label: 'revoked', cls: 'badge-error' };
        if (inv.expires_at && new Date(inv.expires_at) < new Date()) {
            return { label: 'expired', cls: 'badge-warn' };
        }
        if (inv.uses_count >= inv.max_uses) {
            return { label: 'used up', cls: 'badge-warn' };
        }
        return { label: 'active', cls: 'badge-ok' };
    }

    async function onCreate() {
        if (creating) return;
        creating = true;
        createError = null;
        createdInvite = null;
        try {
            const result = await adminCreateInvite({
                expires_in: expiresIn,
                max_uses: maxUses,
                role,
                note: note || null,
            });
            createdInvite = result;
            // Reset form
            note = '';
            await load();
        } catch (e: any) {
            createError = e.message || 'Failed to create invite';
        } finally {
            creating = false;
        }
    }

    async function onRevoke(inv: AdminInvite) {
        if (!window.confirm(`Revoke invite "${inv.note || inv.token_masked}"? Any unused signup attempts will fail.`)) return;
        if (busyId) return;
        busyId = inv.id;
        error = null;
        try {
            await adminRevokeInvite(inv.id);
            await load();
        } catch (e: any) {
            error = e.message || 'Revoke failed';
        } finally {
            busyId = null;
        }
    }

    function dismissCreated() {
        createdInvite = null;
    }

    const unsubUser = currentUser.subscribe(() => { load(); });
    const unsubReady = authReady.subscribe(() => { load(); });
    onDestroy(() => { unsubUser(); unsubReady(); });
</script>

<div class="admin-page">
    <header class="admin-header">
        <span class="eyebrow">Admin · Invites</span>
        <h1>Signup links</h1>
        <p class="page-sub">Generate single-use or limited-use signup links and revoke them as needed.</p>
    </header>

    <AdminNav active="invites" />

    <!-- Create form -->
    <section class="block create-block">
        <div class="block-head"><h2>Create invite</h2></div>
        <form on:submit|preventDefault={onCreate} class="create-form">
            <label class="ff">
                <span class="field-label">Expires in</span>
                <select bind:value={expiresIn} disabled={creating}>
                    <option value="30m">30 minutes</option>
                    <option value="24h">24 hours</option>
                    <option value="7d">7 days</option>
                    <option value="30d">30 days</option>
                </select>
            </label>
            <label class="ff">
                <span class="field-label">Max uses</span>
                <input type="number" min="1" max="1000" bind:value={maxUses} disabled={creating} />
            </label>
            <label class="ff">
                <span class="field-label">Role</span>
                <select bind:value={role} disabled={creating}>
                    <option value="volunteer">volunteer</option>
                    <option value="admin">admin</option>
                </select>
            </label>
            <label class="ff note-field">
                <span class="field-label">Note (optional)</span>
                <input type="text" bind:value={note} maxlength="200" disabled={creating}
                       placeholder="e.g. 'For Mary'" />
            </label>
            <button type="submit" disabled={creating} class="btn-primary">
                {creating ? 'Creating…' : 'Create invite →'}
            </button>
        </form>
        {#if createError}
            <p class="msg msg-error">{createError}</p>
        {/if}

        {#if createdInvite}
            <div class="created-banner">
                <div class="created-head">
                    <span><span class="eyebrow">Invite created</span></span>
                    <button class="dismiss" on:click={dismissCreated} aria-label="Dismiss">×</button>
                </div>
                <p class="created-sub">
                    Role: <strong>{createdInvite.invite.role}</strong> ·
                    Max uses: <strong>{createdInvite.invite.max_uses}</strong> ·
                    Expires <span class="mono">{fmtTime(createdInvite.invite.expires_at)}</span>
                </p>
                <CopyableUrl value={createdInvite.signup_url} label="Signup URL" />
            </div>
        {/if}
    </section>

    <section class="block">
        <div class="block-head"><h2>Existing invites</h2></div>
        {#if error}
            <p class="msg msg-error">{error}</p>
        {/if}
        {#if loading}
            <p class="loading">Loading…</p>
        {:else if invites.length === 0}
            <p class="muted">No invites yet — use the form above to create one.</p>
        {:else}
            <div class="table-wrap">
                <table>
                    <thead>
                        <tr>
                            <th>Token</th>
                            <th>Role</th>
                            <th>Status</th>
                            <th class="num">Uses</th>
                            <th>Expires</th>
                            <th>Note</th>
                            <th>Created</th>
                            <th class="actions-th">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {#each invites as inv (inv.id)}
                            {@const st = inviteState(inv)}
                            <tr>
                                <td><code>{inv.token_masked}</code></td>
                                <td>
                                    <span class="badge {inv.role === 'admin' ? 'badge-admin' : 'badge-vol'}">
                                        {inv.role}
                                    </span>
                                </td>
                                <td><span class="badge {st.cls}">{st.label}</span></td>
                                <td class="num mono">{inv.uses_count} / {inv.max_uses}</td>
                                <td class="mono">{fmtTime(inv.expires_at)}</td>
                                <td class="note-cell">{inv.note ?? '—'}</td>
                                <td class="mono">{fmtTime(inv.created_at)}</td>
                                <td class="actions">
                                    {#if !inv.revoked_at}
                                        <button class="row-btn" on:click={() => onRevoke(inv)} disabled={busyId === inv.id}>
                                            Revoke
                                        </button>
                                    {:else}
                                        <span class="muted-dash">—</span>
                                    {/if}
                                </td>
                            </tr>
                        {/each}
                    </tbody>
                </table>
            </div>
        {/if}
    </section>
</div>

<style>
    .admin-page {
        max-width: 1180px;
        margin: 0 auto;
        padding: var(--s-7) var(--s-5) var(--s-8);
    }
    .admin-header { margin-bottom: var(--s-5); }
    .admin-header .eyebrow { color: var(--leaf-bright); display: block; margin-bottom: var(--s-2); }
    .admin-header h1 {
        font-family: var(--font-display);
        font-size: clamp(2rem, 5vw, 2.8rem);
        margin: 0 0 var(--s-2); font-weight: 600;
    }
    .page-sub { color: var(--ink-soft); font-size: 1rem; margin: 0; }

    .block { margin-top: var(--s-7); }
    .block-head {
        margin-bottom: var(--s-4);
        padding-bottom: var(--s-3);
        border-bottom: 1px solid var(--rule);
    }
    .block-head h2 {
        font-family: var(--font-display);
        font-size: 1.3rem;
        font-weight: 500;
        margin: 0;
    }

    .loading { color: var(--ink-soft); padding: var(--s-5) 0; }
    .muted { color: var(--ink-muted); font-style: italic; }
    .muted-dash { color: var(--ink-muted); }

    .msg { margin: var(--s-3) 0 0; padding: 10px 14px; font-size: 0.9rem; border-left: 3px solid; }
    .msg-error { background: var(--alert-soft); border-color: var(--alert); color: var(--alert); }

    /* Create form */
    .create-block {
        margin-top: var(--s-5);
        background: var(--field);
        border: 1px solid var(--rule);
        padding: var(--s-5) var(--s-5) var(--s-4);
    }
    .create-block .block-head {
        margin-bottom: var(--s-4);
        padding-bottom: var(--s-3);
        border-bottom: 1px solid var(--rule);
    }
    .create-form {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
        gap: var(--s-3);
        align-items: end;
    }
    .ff { display: flex; flex-direction: column; gap: var(--s-2); }
    .note-field { grid-column: span 2; }
    @media (max-width: 700px) { .note-field { grid-column: auto; } }
    .field-label {
        font-size: 0.72rem;
        text-transform: uppercase;
        letter-spacing: 0.1em;
        color: var(--ink-muted);
        font-weight: 600;
    }
    .create-form input,
    .create-form select {
        font-family: var(--font-body);
        padding: 10px 12px;
        font-size: 0.95rem;
        background: var(--paper-soft);
        border: 1px solid var(--rule);
        border-bottom: 2px solid var(--rule-strong);
        color: var(--ink);
        transition: border-color 0.12s, box-shadow 0.12s, background 0.12s;
    }
    .create-form input:focus,
    .create-form select:focus {
        outline: none;
        border-color: var(--leaf-bright);
        background: var(--field);
        box-shadow: 0 0 0 3px rgba(45, 122, 79, 0.12);
    }

    .btn-primary {
        font-family: var(--font-body);
        padding: 11px 18px;
        background: var(--ink);
        color: var(--paper);
        border: 1px solid var(--ink);
        font-size: 0.9rem;
        font-weight: 600;
        cursor: pointer;
        align-self: end;
        transition: background 0.15s;
    }
    .btn-primary:hover:not(:disabled) { background: var(--leaf); border-color: var(--leaf); }
    .btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }

    .created-banner {
        margin-top: var(--s-4);
        border: 1px solid var(--leaf-bright);
        border-left: 4px solid var(--leaf);
        background: var(--leaf-soft);
        padding: var(--s-4);
    }
    .created-head {
        display: flex; justify-content: space-between; align-items: center;
        margin-bottom: 4px;
    }
    .created-head .eyebrow { color: var(--leaf-ink); }
    .created-sub { margin: 0 0 var(--s-3); font-size: 0.85rem; color: var(--leaf-ink); }
    .dismiss {
        background: transparent; border: 0; font-size: 1.4rem; line-height: 1;
        cursor: pointer; color: var(--ink-soft); padding: 0 6px;
    }
    .dismiss:hover { color: var(--ink); }

    /* Tables */
    .table-wrap { background: var(--field); border: 1px solid var(--rule); overflow-x: auto; }
    table { width: 100%; border-collapse: collapse; font-size: 0.9rem; min-width: 880px; }
    th, td {
        text-align: left;
        padding: 12px 14px;
        border-bottom: 1px solid var(--rule);
        vertical-align: middle;
    }
    tbody tr:last-child td { border-bottom: none; }
    tbody tr:hover { background: var(--paper-soft); }
    th {
        font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.1em;
        color: var(--ink-muted); font-weight: 600; background: var(--paper);
        border-bottom: 2px solid var(--rule); white-space: nowrap;
    }
    th.num, td.num { text-align: right; }
    .actions-th { text-align: right; }
    .actions { text-align: right; }
    .note-cell { max-width: 240px; word-break: break-word; }
    code {
        font-size: 0.82rem;
        background: var(--paper);
        padding: 2px 6px;
        border: 1px solid var(--rule);
        font-family: var(--font-mono);
    }

    /* Badges */
    .badge {
        display: inline-block;
        padding: 3px 10px;
        font-size: 0.72rem; font-weight: 600;
        letter-spacing: 0.04em; text-transform: lowercase;
        border: 1px solid;
    }
    .badge-ok    { background: var(--leaf-soft);  color: var(--leaf-ink); border-color: var(--leaf-bright); }
    .badge-error { background: var(--alert-soft); color: var(--alert);    border-color: var(--alert); }
    .badge-warn  { background: var(--sun-soft);   color: var(--ink);      border-color: var(--sun); }
    .badge-admin { background: var(--sun-soft);   color: var(--ink);      border-color: var(--sun); }
    .badge-vol   { background: var(--paper);      color: var(--ink-soft); border-color: var(--rule-strong); }

    /* Row buttons */
    .row-btn {
        font-family: var(--font-body);
        padding: 5px 12px;
        font-size: 0.78rem;
        font-weight: 600;
        border: 1px solid var(--rule-strong);
        background: var(--field);
        color: var(--ink);
        cursor: pointer;
        transition: background 0.12s, color 0.12s, border-color 0.12s;
    }
    .row-btn:hover:not(:disabled) {
        background: var(--alert);
        color: var(--paper);
        border-color: var(--alert);
    }
    .row-btn:disabled { opacity: 0.45; cursor: not-allowed; }
</style>
